import { Button, Stack } from '@mui/material';

export const LIST_DELIMITER = ' || ';
export const MISSING_VALUE = '--';

export function jsonReplacer(key, value) {
  if (this[key] instanceof Date) return `d:${this[key].getTime()}`;
  if (value === undefined) return null;
  return value;
}

function jsonReviver(key, value) {
  if (typeof value === 'string' && value.startsWith('d:'))
    return new Date(Number.parseInt(value.substring(2), 10));
  if (value === null) return '';
  return value;
}

export function tryParse(jsonish) {
  if (jsonish === null) return [];
  try {
    return JSON.parse(jsonish, jsonReviver);
  } catch {
    return [];
  }
}

function idfn(x) {
  return x.id;
}

export const defaultTableOptions = {
  getRowId: idfn,
  enableExpandAll: false,
  enableFacetedValues: true,
  enableGrouping: true,
  enableColumnFilterModes: false,
  // Not using align: center because it makes the table render too wide for the page
  muiTableHeadCellProps: {
    sx: {
      textAlign: 'center',
      verticalAlign: 'middle',
      '& .Mui-TableHeadCell-Content': {
        justifyContent: 'center',
      },
    },
  },
  muiTableBodyCellProps: { align: 'center' },
  muiFilterDateTimePickerProps: {
    disableFuture: true,
    timeSteps: { minutes: 15 },
  },
  // sticky header
  enableStickyHeader: true,
  // global filter
  enableGlobalFilter: true,

  // Always show a count of all items, not just those that pass the filter (default MRT behavior). Need to showAlertBanner in state to ensure it's always present
  renderToolbarAlertBannerContent: ({ table }) => {
    const totalRowCount = Object.values(table.getCoreRowModel().flatRows).filter(Boolean).length;
    const selectedRowCount = Object.values(table.getSelectedRowModel().flatRows).filter(
      Boolean
    ).length;
    const { localization } = table.options;
    return (
      <Stack alignItems="center" direction="row" gap="16px" sx={{ padding: '16px' }}>
        {localization.selectedCountOfRowCountRowsSelected
          ?.replace('{selectedCount}', selectedRowCount.toLocaleString(localization.language))
          ?.replace('{rowCount}', totalRowCount.toLocaleString(localization.language))}
        {selectedRowCount > 0 ? (
          <Button
            // Cannot use getMRTSelectAllHandler({ table })(event, false, true)} as it only unslected visible items
            onClick={() => table.resetRowSelection()}
            size="small"
            sx={{ p: '2px' }}
          >
            {localization.clearSelection}
          </Button>
        ) : null}
      </Stack>
    );
  },
};

export const tableSelectionOptions = {
  // selection
  autoResetPageIndex: false,
  enableBatchRowSelection: true,
  enableMultiRowSelection: true,
  enableRowSelection: true,
  enableSelectAll: true,
  selectAllMode: 'all',
};

export const tableCellularEditingOptions = {
  editDisplayMode: 'cell',
  // optionally, use single-click to activate editing mode instead of default double-click
  muiTableBodyCellProps: ({ cell, table }) => ({
    // repeat the base align center here as well:wq
    align: 'center',
    onClick: () => {
      table.setEditingCell(cell); // set editing cell
    },
  }),
};

export function defaultTableInitialState({
  columnFilters,
  sorting = [],
  hiddenColumns = [],
  forcedHiddenColumns = [],
  expanded = {},
}) {
  const columnVisibility = columnFilters
    ? Object.fromEntries(
        hiddenColumns
          .filter((k) => !columnFilters.map(({ id }) => id).includes(k))
          .concat(forcedHiddenColumns)
          .map((k) => [k, false])
      )
    : null;
  return Object.fromEntries(
    Object.entries({
      columnFilters,
      sorting,
      showColumnFilters: true,
      expanded,
      columnVisibility,
    }).filter((e) => e[1] !== null)
  );
}

export function extractSortingParams(searchParams) {
  return Object.entries(tryParse(searchParams.get('sortBy'))).map(([id, desc]) => ({ id, desc }));
}

function deserializeValue(value) {
  //  if (Array.isArray(value)) return value.map((v) => deserializeValue(v))
  //  if (value === '') return undefined;
  return value;
}

function serializeValue(value) {
  //  if (Array.isArray(value)) return value.map((v) => serializeValue(v) || '')
  //  if (value instanceof Date) return value.getTime();
  return value;
}

export function extractFilterParams(searchParams) {
  const filters = Object.entries(tryParse(searchParams.get('filterBy'))).map(([id, value]) => ({
    id,
    value: deserializeValue(value),
  }));
  return filters;
}

export function extractExpandedParams(searchParams) {
  return Object.fromEntries(tryParse(searchParams.get('expanded', '[]')).map((id) => [id, true]));
}

function identity(params) {
  return params;
}

export function sortingUpdater(sorting) {
  return (updater) => {
    let newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting !== sorting) {
      if (newSorting.length === 0)
        return (params) => {
          params.delete('sortBy');
          return params;
        };
      newSorting = Object.fromEntries(newSorting.map(({ id, desc }) => [id, desc]));
      return (params) => {
        params.set('sortBy', JSON.stringify(newSorting));
        return params;
      };
    }
    return identity;
  };
}

export function prepareUrlColumnFilters(newColumnFilters) {
  return JSON.stringify(newColumnFilters, jsonReplacer);
}

export function filterUpdater(columnFilters) {
  return (updater) => {
    let newColumnFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    if (newColumnFilters !== columnFilters) {
      newColumnFilters = Object.fromEntries(
        newColumnFilters
          .map(({ id, value }) => [id, serializeValue(value)])
          .filter((idv) => !Array.isArray(idv[1]) || !(idv[1][0] === '' && idv[1][1] === ''))
      );
      if (Object.keys(newColumnFilters).length === 0)
        return (params) => {
          params.delete('filterBy');
          return params;
        };
      return (params) => {
        params.set('filterBy', prepareUrlColumnFilters(newColumnFilters));
        return params;
      };
    }
    return identity;
  };
}

export function setExpanded(updater, expanded, setSearchParams) {
  let newExpanded = typeof updater === 'function' ? updater(expanded) : updater;

  if (newExpanded !== expanded) {
    newExpanded = Object.entries(newExpanded)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value)
      // eslint-disable-next-line no-unused-vars
      .map(([key, _]) => key);
    if (newExpanded.length === 0)
      setSearchParams((params) => {
        params.delete('expanded');
        return params;
      });
    else {
      setSearchParams((params) => {
        params.set('expanded', JSON.stringify(newExpanded));
        return params;
      });
    }
  }
}

export const parameterUpdater = (key, updater, updaterOrig) => (params) => {
  // Okay to mutate, this is working on a copy
  const values = typeof updater === 'function' ? updater(updaterOrig) : updater;
  params.set(key, JSON.stringify(values));
  return params;
};
export function convertSelectionToList(selection) {
  return (
    Object.entries(selection)
      // eslint-disable-next-line no-unused-vars
      .filter(([_k, v]) => v)
      // eslint-disable-next-line no-unused-vars
      .map(([k, _v]) => parseInt(k, 10) ?? k)
  );
}

export function percentileColor(percent) {
  let quantile = percent;
  if (quantile > 0 && quantile < 1) quantile *= 100;
  if (Number.isNaN(quantile) || quantile < 0 || quantile === MISSING_VALUE) {
    return 'primary'; // Grey for NaN / negative
  }
  if (quantile > 80) {
    return 'success'; // Green color for values above 80
  }
  if (quantile > 50) {
    return 'warning'; // Yellow color for values between 50 and 80
  }
  return 'error'; // Red color for values below or equal to 50
}
